import { Alert, Checkbox, Field, Icon, Select } from '@kontentino/ui';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { PageData } from 'types/Page';
import Logger from 'utils/logger';
import DuplicatePostPreview from '../../modules/duplicatePosts/components/DuplicatePostPreview';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import { ApiClientError } from 'api/client';
import ContentPlanApi from 'app/api/contentPlan';
import PostTimeSaversApi, {
  MovePostSettledResult,
  ShiftToProjectParams,
} from 'app/api/postTimeSavers';
import { getPostTargetInfoMessage } from 'app/modules/posts/utils/getPostTargetInfoMessage';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import UserpilotUtils from 'app/utils/userpilot';
import { queryKey } from 'constants/queryKey';
import routes, { modalRoutes } from 'constants/routes';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useMutation, useQuery } from 'react-query';
import { Post } from 'types/Post';
import {
  STATUS,
  TYPE as POST_TYPE,
} from '@kontentino/kontentino-constants/Posts';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { ListPost } from 'types/PostDetail';
import useSetState from 'utils/hooks/useSetState';
import { createSearchFromParams, getBasePath } from 'utils/url';
import { ShiftPostsTarget } from '../../hooks/useShiftPosts';
import { ShiftPostsTargetValidationError } from './ShiftTargetProfilesSelect';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import ShiftTargetSuccessful from './shiftTargetProfilesSelect/shiftPostToTarget/ShiftTargetSuccessful';
import ShiftTargetUnsuccessful from './shiftTargetProfilesSelect/shiftPostToTarget/ShiftTargetUnsuccessful';
import ShiftTargetProjectPageSelector from './shiftTargetProfilesSelect/shiftPostToTarget/shiftTargetProjectOfProfileSelect/ShiftTargetProjectPageSelector';
import dayjs from 'dayjs';
import clsx from 'clsx';

interface Props {
  availablePages: PageData[];
  targetIndex: number;
  initialTarget: ShiftPostsTarget;
  removeTarget?: (id: string) => void;
  ref: React.Ref<TargetHandler>;
  dataNamePrefix: string;
  error?: ShiftPostsTargetValidationError;
  selectedPosts: ListPost[];
}

export type TargetHandler = {
  getValidationError(): ShiftPostsTargetValidationError;
  submitDuplication(ids: number[]): void;
};

type PostState = ListPost & {
  date?: string | null;
  time?: string | null;
};

const TargetProfileForm: React.FC<Props> = forwardRef(
  (
    {
      availablePages,
      targetIndex,
      initialTarget,
      removeTarget,
      dataNamePrefix,
      error,
      selectedPosts,
    },
    ref: React.Ref<TargetHandler>,
  ) => {
    const isNewPostExperienceActive = usePostsLayoutState(
      (state) => state.isNewPostExperienceActive,
    );
    const [useCustomSchedule, setUseCustomSchedule] = useState(false);
    const [postState, setPostState] = useState<PostState[]>(
      selectedPosts.map((post) => ({
        ...post,
        date: dayjs().startOf('day').format('YYYY-MM-DD'),
        time: null,
      })),
    );
    const { t } = useTranslation();
    const { triggerG2Review } = useG2ReviewTrigger();

    type ShiftPostResponse = (Post & { warning?: string | null })[];

    const shiftPost = useMutation<
      ShiftPostResponse | MovePostSettledResult[],
      ApiClientError,
      ShiftToProjectParams
    >(PostTimeSaversApi.shiftToProject, {
      onSuccess() {
        UserpilotUtils.track(UserpilotUtils.events.POSTS_POST_DUPLICATED);
        triggerG2Review();
      },
      onError(error) {
        Logger.error(error);
      },
    });

    const shouldCloneAsDraftByDefault = (
      currentProfileId: number | undefined,
    ): boolean => {
      if (!currentProfileId) return false;

      const targetPage = availablePages.find(
        (page) => page.id === currentProfileId,
      );

      if (targetPage?.type === TYPE.FACEBOOK) {
        const hasFacebookPostExportedStatus = selectedPosts.some(
          (post) =>
            post.page.type === TYPE.FACEBOOK && post.status === STATUS.EXPORTED,
        );

        if (hasFacebookPostExportedStatus) {
          return true;
        }
      }

      return selectedPosts.some(
        (post) =>
          post.status === STATUS.POSTED || post.status === STATUS.FAILED,
      );
    };

    const shouldDisplayMentionsAlert = (
      currentProfileId: number | undefined,
    ): boolean => {
      if (!currentProfileId) return false;

      const targetPage = availablePages.find(
        (page) => page.id === currentProfileId,
      );
      const riskyMentionsTypes = [TYPE.FACEBOOK, TYPE.LINKEDIN] as number[];

      return (
        !!targetPage &&
        selectedPosts.some(
          (post) =>
            post.status === STATUS.SCHEDULED &&
            riskyMentionsTypes.includes(post.page.type) &&
            post.page.type !== targetPage.type,
        )
      );
    };

    const [targetProfile, setTargetProfile] = useSetState({
      profileId: initialTarget.profileId,
      projectId: initialTarget.projectId,
      cloneAsDraft: shouldCloneAsDraftByDefault(initialTarget.profileId),
    });

    useEffect(() => {
      const defaultCloneAsDraftForNewProps = shouldCloneAsDraftByDefault(
        initialTarget.profileId,
      );

      setTargetProfile({
        profileId: initialTarget.profileId,
        projectId: initialTarget.projectId,
        cloneAsDraft: defaultCloneAsDraftForNewProps,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTarget, setTargetProfile]);

    useEffect(() => {
      setTargetProfile((prevTargetProfile) => {
        const newCloneAsDraftValue = prevTargetProfile.profileId
          ? shouldCloneAsDraftByDefault(prevTargetProfile.profileId)
          : false;
        return { cloneAsDraft: newCloneAsDraftValue };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetProfile.profileId, setTargetProfile]);

    const profile = useMemo(() => {
      return availablePages.find((page) => page.id === targetProfile.profileId);
    }, [availablePages, targetProfile.profileId]);

    const availablePagesIds = availablePages.map((page) => page.id);

    const { data: projectsData, isLoading: areProjectsLoading } = useQuery(
      queryKey.postsContentPlans(availablePagesIds),
      () => ContentPlanApi.getPagesContentPlans(availablePagesIds),
    );

    const projects = useMemo(() => {
      if (!projectsData?.data || !targetProfile.profileId) return [];
      return projectsData.data[targetProfile.profileId] || [];
    }, [projectsData, targetProfile.profileId]);

    const project = useMemo(() => {
      if (targetProfile.projectId) {
        return projects?.find(
          (project) => project.id === targetProfile.projectId,
        );
      }
    }, [projects, targetProfile]);

    useEffect(() => {
      if (projects.length > 0 && !targetProfile.projectId) {
        setTargetProfile({ projectId: projects[0].id });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, setTargetProfile]);

    const duplicatePostAsDraft = () => {
      setTargetProfile({
        cloneAsDraft: !targetProfile.cloneAsDraft,
      });
    };

    const changeProfile = (profileId: number) => {
      let projectId = undefined;

      if (profileId) {
        const profileProjects = projectsData?.data[profileId];

        if (profileProjects) {
          projectId = profileProjects[0].id;
        }
      }

      setTargetProfile({ profileId, projectId });
    };

    useImperativeHandle(ref, () => ({
      getValidationError: () => ({
        id: initialTarget.id,
        profileId: targetProfile.profileId ? undefined : t('isRequired'),
        projectId: areProjectsLoading
          ? t('pleaseWaitProjectIsLoading')
          : targetProfile.projectId
          ? undefined
          : t('isRequired'),
      }),
      submitDuplication: (postIds: number[]) => {
        if (targetProfile.projectId) {
          const postsCustomSchedule = useCustomSchedule
            ? Object.fromEntries(
                postState
                  .filter(
                    (post) =>
                      postIds.includes(post.id) && (post.date || post.time),
                  )
                  .map((post) => [
                    post.id,
                    {
                      customScheduleDate: post.date || undefined,
                      customScheduleTime: post.time || undefined,
                    },
                  ]),
              )
            : undefined;

          shiftPost.mutate({
            variant: 'duplicate',
            ids: postIds,
            projectId: targetProfile.projectId,
            cloneAsDraft: targetProfile.cloneAsDraft,
            postsCustomSchedule:
              postsCustomSchedule && Object.keys(postsCustomSchedule).length > 0
                ? postsCustomSchedule
                : undefined,
          });
        }
      },
    }));

    if (shiftPost.isError) {
      return (
        <ShiftTargetUnsuccessful
          page={profile}
          projectName={project?.name}
          additionalMessage={shiftPost?.error?.userMessage}
          errorMessage={t('postsShift.unableToDuplicatePosts')}
        />
      );
    }

    if (shiftPost.data?.length) {
      const posts = shiftPost.data as ShiftPostResponse;
      const contentPlanUrl = `${getBasePath()}${
        routes.POSTS_CALENDAR
      }${createSearchFromParams({
        profiles: targetProfile.profileId,
        project: targetProfile.projectId,
      })}`;

      const firstPostUrl = `${getBasePath()}${
        routes.POSTS_CALENDAR
      }${createSearchFromParams({
        id: posts[0].id,
        profiles: targetProfile.profileId,
      })}#${
        isNewPostExperienceActive
          ? modalRoutes.POST_DETAIL_NEW
          : modalRoutes.POST_DETAIL
      }`;

      const isSinglePost = posts.length === 1;
      const additionalMessage = isSinglePost
        ? getPostTargetInfoMessage(selectedPosts[0], posts[0])
        : '';

      return (
        <ShiftTargetSuccessful
          infoMessage={additionalMessage}
          urlToOpen={isSinglePost ? firstPostUrl : contentPlanUrl}
          label={
            isSinglePost
              ? t('postsShift.showPost')
              : t('postsShift.showContentPlan')
          }
          dataNamePrefix={dataNamePrefix}
          projectName={project?.name}
          page={profile}
          warningMessage={posts[0].warning}
        />
      );
    }

    const handleUpdatePostDate = (value: string, postId: number) => {
      setPostState((prev) => {
        const newPostsState = [...prev];
        const postIndex = newPostsState.findIndex((post) => post.id === postId);
        if (postIndex !== -1) {
          newPostsState[postIndex] = {
            ...newPostsState[postIndex],
            date: value,
          };
        }
        return newPostsState;
      });
    };

    const handleUpdatePostTime = (value: string, postId: number) => {
      setPostState((prev) => {
        const newPostsState = [...prev];
        const postIndex = newPostsState.findIndex((post) => post.id === postId);
        if (postIndex !== -1) {
          newPostsState[postIndex] = {
            ...newPostsState[postIndex],
            time: value,
          };
        }
        return newPostsState;
      });
    };

    if (shiftPost.isLoading) {
      return <Skeleton width="100%" height={85} />;
    }

    return (
      <div
        className="tw-relative tw-rounded-md tw-bg-grayscale-5 tw-p-4"
        data-cy={`${dataNamePrefix}_target-profile-form-${targetIndex}`}
      >
        {removeTarget && (
          <Icon
            icon={faClose}
            className="tw-absolute tw-right-3 tw-top-3 tw-cursor-pointer"
            onClick={() => removeTarget(initialTarget.id)}
          />
        )}

        <form className="tw-flex tw-flex-col tw-gap-y-4">
          <Field.Group>
            <Field.Label required>
              {targetIndex}. {t('postsShift.targetProfile')}
            </Field.Label>
            <div
              data-name={`${dataNamePrefix}_profile-input`}
              data-cy={`${dataNamePrefix}_profile-input`}
            >
              <ShiftTargetProjectPageSelector
                pages={availablePages}
                onProfileChange={(profileId) => changeProfile(profileId)}
                value={availablePages.find(
                  (page) => page.id === targetProfile.profileId,
                )}
              />
            </div>
            <Field.Error>{error?.profileId}</Field.Error>
          </Field.Group>
          {targetProfile.profileId && (
            <>
              <Field.Group>
                <Field.Label required>{t('contentPlan')}</Field.Label>

                <div
                  data-name={`${dataNamePrefix}_content-plan-input`}
                  data-cy={`${dataNamePrefix}_content-plan-input`}
                >
                  <Select
                    key={targetProfile.profileId}
                    isSearchable
                    value={
                      project
                        ? { label: project.name, value: project.id }
                        : null
                    }
                    defaultValue={null}
                    placeholder={t('selectContentPlan')}
                    options={projects.map((project) => ({
                      value: project.id,
                      label: project.name,
                    }))}
                    onChange={(selectedProject) => {
                      if (selectedProject) {
                        setTargetProfile({ projectId: selectedProject.value });
                      }
                    }}
                    isDisabled={!targetProfile.profileId || areProjectsLoading}
                    isLoading={areProjectsLoading}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                      valueContainer: (css) => ({
                        ...css,
                        input: {
                          position: 'absolute',
                        },
                      }),
                    }}
                  />
                </div>
              </Field.Group>
              <Field.Error>{error?.projectId}</Field.Error>
              {shouldDisplayMentionsAlert(targetProfile.profileId) && (
                <Alert
                  variant="warning"
                  title={'Mentions converted to plain text'}
                >
                  Mentions in this post may not match on the new platform and
                  have been converted to plain text. Please re-add them before
                  publishing.
                </Alert>
              )}
              {selectedPosts.some((post) => post.type === POST_TYPE.DOCUMENT) &&
                profile?.type !== TYPE.LINKEDIN && (
                  <Alert variant="warning">
                    {t('postsShift.documentPostTypeLinkedinOnly', {
                      defaultValue:
                        'Document post type is supported only for LinkedIn. For other social networks, only the post text will be duplicated.',
                    })}
                  </Alert>
                )}
              <Field.Group className="tw-flex !tw-flex-row tw-gap-2">
                <Checkbox
                  size="small"
                  id={`duplicate-as-draft-${targetIndex}`}
                  onChange={duplicatePostAsDraft}
                  checked={
                    shouldCloneAsDraftByDefault(targetProfile.profileId)
                      ? true
                      : targetProfile.cloneAsDraft
                  }
                  disabled={!targetProfile.projectId}
                  data-cy={`${dataNamePrefix}_duplicate-as-draft`}
                  className={clsx({
                    'tw-cursor-not-allowed tw-bg-grayscale-70 hover:tw-bg-grayscale-70':
                      shouldCloneAsDraftByDefault(targetProfile.profileId),
                  })}
                />
                <label
                  htmlFor={`duplicate-as-draft-${targetIndex}`}
                  className={clsx(
                    'tw-text-md tw-font-regular hover:tw-cursor-pointer',
                    {
                      'hover:!tw-cursor-not-allowed':
                        shouldCloneAsDraftByDefault(targetProfile.profileId),
                    },
                  )}
                >
                  {t('postsShift.duplicateAsDraftLabel')}{' '}
                  <span className="tw-text-grayscale-100">
                    {t('postsShift.duplicateAsDraftHint')}
                  </span>
                </label>
              </Field.Group>
              <Field.Group className="tw-flex !tw-flex-row tw-gap-2">
                <Checkbox
                  size="small"
                  id={`use-custom-schedule-${targetIndex}`}
                  checked={useCustomSchedule}
                  onChange={() => setUseCustomSchedule((prev) => !prev)}
                />
                <label
                  htmlFor={`use-custom-schedule-${targetIndex}`}
                  className="tw-text-md tw-font-regular hover:tw-cursor-pointer"
                >
                  {t('postsShift.useCustomDateTime')}
                </label>
              </Field.Group>
            </>
          )}

          {useCustomSchedule &&
            postState.map((post, index) => (
              <DuplicatePostPreview
                key={post.id}
                post={post}
                onDateChange={(date) =>
                  handleUpdatePostDate(
                    date ? dayjs(date).format('YYYY-MM-DD') : '',
                    post.id,
                  )
                }
                onTimeChange={(time) =>
                  handleUpdatePostTime(time || '', post.id)
                }
              />
            ))}
        </form>
      </div>
    );
  },
);

export default TargetProfileForm;
